import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { fetchItemDetails, fetchProductMains, fetchProductSubs, fetchMainCategories, fetchSubCategories, fetchItems, fetchProducts, updateSheetId } from '../services/googleSheets.ts';
import { ItemDetail, ProductMain, ProductSub, Product } from '../types';
import LoadingOverlay from './LoadingOverlay.tsx';
import ImageWithFallback from './ImageWithFallback.tsx';
import SEOHead from './SEOHead.tsx';
import './ItemDetails.css';

import { toast } from 'react-toastify';
import { useCart } from '../hooks/useCart.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useFollow } from '../contexts/FollowContext.tsx';
import styled from 'styled-components';
import LoginModal from './auth/LoginModal.tsx';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

interface CacheData {
    data: any;
    timestamp: number;
}

const cache: { [key: string]: CacheData } = {};

const getCachedData = async (key: string, fetchFn: () => Promise<any>, duration = CACHE_DURATION) => {
    const now = Date.now();
    const cachedData = cache[key];

    if (cachedData && (now - cachedData.timestamp) < duration) {
        return cachedData.data;
    }

    const data = await fetchFn();
    cache[key] = { data, timestamp: now };
    return data;
};

const useItemData = (id: string | undefined, url: string | undefined) => {
    const navigate = useNavigate();

    const fetchData = useCallback(async () => {
        if (!url && !id) {
            navigate('/');
            return null;
        }

        const cacheKey = `item_${id || url}`;

        try {
            if (id) {
                // Directly fetch item details if we have ID
                return await getCachedData(cacheKey, () => fetchItemDetails(id));
            } else {
                // Find item by URL
                return await getCachedData(cacheKey, async () => {
                    const allSubCategories = await fetchMainCategories();
                    for (const mainCategory of allSubCategories) {
                        const subCategories = await fetchSubCategories(mainCategory.id);
                        for (const subCategory of subCategories) {
                            const items = await fetchItems(subCategory.id);
                            const matchingItem = items.find(item => item.url === url);
                            if (matchingItem) {
                                return await fetchItemDetails(matchingItem.id);
                            }
                        }
                    }
                    return null;
                });
            }
        } catch (error) {
            console.error('Error fetching item data:', error);
            return null;
        }
    }, [id, url, navigate]);

    return fetchData;
};

const ItemDetails: React.FC = () => {
    const { url } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    
    // استخراج المعرف من location.state أو من معلمات الاستعلام في URL
    const searchParams = new URLSearchParams(location.search);
    const queryItemId = searchParams.get('item_id');
    const querySheetId = searchParams.get('sheet_id');
    
    // تفضيل معرف العنصر من معلمات الاستعلام إذا كان متاحًا
    const { id: stateId } = location.state || {};
    const id = queryItemId || stateId;
    
    const [loading, setLoading] = useState(true);
    const [item, setItem] = useState<ItemDetail | null>(null);
    const [productMains, setProductMains] = useState<ProductMain[]>([]);
    const [productSubs, setProductSubs] = useState<ProductSub[]>([]);
    const [activeMainId, setActiveMainId] = useState<string>('all');
    const [selectedImage, setSelectedImage] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeSubId, setActiveSubId] = useState<Record<string, string | null>>({});
    const [products, setProducts] = useState<Product[]>([]);
    const { addToCart } = useCart();
    const [allProducts, setAllProducts] = useState<Product[]>([]);
    const [allProductSubs, setAllProductSubs] = useState<ProductSub[]>([]);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const { isAuthenticated } = useAuth();
    const { followedItems, followItem, unfollowItem, isFollowing } = useFollow();

    const fetchItemData = useItemData(id, url);

    // إذا تم تمرير معرف شيت جديد، قم بتحديثه
    useEffect(() => {
        if (querySheetId) {
            console.log('Updating sheet ID from URL query parameter:', querySheetId);
            updateSheetId(querySheetId);
        }
    }, [querySheetId]);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                console.log('Loading item data with ID:', id, 'and URL:', url);
                
                const itemData = await fetchItemData();

                if (!itemData) {
                    console.error('No item data found, navigating to home');
                    navigate('/');
                    return;
                }

                setItem(itemData);
                if (itemData.images?.length > 0) {
                    setSelectedImage(itemData.images[0]);
                }

                // Parallel data fetching for categories and products
                const mainsData = await fetchProductMains(itemData.id);
                setProductMains(mainsData);

                if (mainsData.length > 0) {
                    // Fetch all data in parallel
                    const [allSubs, allProductsData] = await Promise.all([
                        // Fetch all subcategories in parallel
                        Promise.all(mainsData.map(main =>
                            getCachedData(`subs_${main.id}`, () => fetchProductSubs(main.id))
                        )).then(results => results.flat()),

                        // Pre-fetch all products for all subcategories
                        Promise.all(mainsData.map(async main => {
                            const subs = await fetchProductSubs(main.id);
                            return Promise.all(subs.map(sub =>
                                getCachedData(`products_${sub.id}`, () => fetchProducts(sub.id))
                            ));
                        })).then(results => results.flat(2))
                    ]);

                    setAllProductSubs(allSubs);
                    setProductSubs(allSubs);
                    setAllProducts(allProductsData);
                    setProducts(allProductsData);
                }
            } catch (error) {
                console.error('Error loading data:', error);
                toast.error('حدث خطأ أثناء تحميل البيانات');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [url, id, navigate, fetchItemData]);

    //console.log(productMains)
    //console.log(productSubs)
    useEffect(() => {
        if (item?.Meta_Title) {
            // تحديث عنوان الصفحة
            document.title = item.Meta_Title;

            // تحديث الميتا تاغز
            const updateMetaTag = (selector: string, content: string) => {
                let element = document.querySelector(selector);
                if (!element) {
                    element = document.createElement('meta');
                    const isOg = selector.includes('og:');
                    if (isOg) {
                        element.setAttribute('property', selector.replace('meta[property="', '').replace('"]', ''));
                    } else {
                        element.setAttribute('name', selector.replace('meta[name="', '').replace('"]', ''));
                    }
                    element.setAttribute('data-rh', 'true');
                    document.head.appendChild(element);
                }
                element.setAttribute('content', content);
            };

            // تحديث جميع الميتا تاغز
            const metaTags = {
                'meta[name="description"]': item.Meta_Description || '',
                'meta[name="keywords"]': item.Meta_key_Words || '',
                'meta[property="og:title"]': item.Meta_Title,
                'meta[property="og:description"]': item.Meta_Description || '',
                'meta[property="og:image"]': item.images?.[0] || '%PUBLIC_URL%/logo512.png',
                'meta[property="og:url"]': window.location.href,
                'meta[property="og:type"]': 'website',
                'meta[name="twitter:card"]': 'summary_large_image',
                'meta[name="twitter:title"]': item.Meta_Title,
                'meta[name="twitter:description"]': item.Meta_Description || '',
                'meta[name="twitter:image"]': item.images?.[0] || '%PUBLIC_URL%/logo512.png'
            };

            // تحديث كل ميتا تاغ
            Object.entries(metaTags).forEach(([selector, content]) => {
                updateMetaTag(selector, content);
            });

            // تحديث canonical URL
            let canonicalElement = document.querySelector('link[rel="canonical"]');
            if (!canonicalElement) {
                canonicalElement = document.createElement('link');
                canonicalElement.setAttribute('rel', 'canonical');
                document.head.appendChild(canonicalElement);
            }
            canonicalElement.setAttribute('href', window.location.href);
        }

        // Cleanup function
        return () => {
            document.title = 'Wasal City - وصال سيتي';
            const defaultDescription = 'وصال سيتي - منصة متكاملة توفر معلومات وخدمات متنوعة';
            const defaultImage = '%PUBLIC_URL%/logo512.png';

            const updateMetaTag = (selector: string, content: string) => {
                const element = document.querySelector(selector);
                if (element) {
                    element.setAttribute('content', content);
                }
            };

            // Reset meta tags to default values
            updateMetaTag('meta[name="description"]', defaultDescription);
            updateMetaTag('meta[property="og:title"]', 'Wasal City - وصال سيتي');
            updateMetaTag('meta[property="og:description"]', defaultDescription);
            updateMetaTag('meta[property="og:image"]', defaultImage);
            updateMetaTag('meta[name="twitter:title"]', 'Wasal City - وصال سيتي');
            updateMetaTag('meta[name="twitter:description"]', defaultDescription);
            updateMetaTag('meta[name="twitter:image"]', defaultImage);
        };
    }, [item]);

    const handleMainCategoryClick = (mainId: string) => {
        setActiveMainId(mainId);
        setActiveSubId({}); // Reset active sub category when changing main category

        if (mainId !== 'all') {
            // Filter subcategories for the selected main category
            const subs = allProductSubs.filter(sub => sub.productMainId === mainId);
            setProductSubs(subs);

            // Filter products for these subcategories
            const filteredProducts = allProducts.filter(product =>
                subs.some(sub => sub.id === product.productSubId)
            );
            setProducts(filteredProducts);
        } else {
            // Show all subcategories and products
            setProductSubs(allProductSubs);
            setProducts(allProducts);
        }
    };

    const handleSubCategoryClick = (mainId: string, subId: string | null) => {
        // إذا كانت الفئة الرئيسية المحددة هي "الكل" وتم اختيار فئة فرعية
        // قم بتحديد الفئة الرئيسية التي تنتمي إليها الفئة الفرعية
        if (activeMainId === 'all' && subId !== null) {
            // تحديد الفئة الرئيسية التي تنتمي إليها الفئة الفرعية المختارة
            const subCategory = allProductSubs.find(sub => sub.id === subId);
            if (subCategory) {
                // تحديث الفئة الرئيسية النشطة
                setActiveMainId(mainId);

                // تحديث قائمة الفئات الفرعية لتعرض فقط الفئات الفرعية التابعة للفئة الرئيسية المختارة
                const subs = allProductSubs.filter(sub => sub.productMainId === mainId);
                setProductSubs(subs);

                // تحديث قائمة المنتجات لتعرض فقط المنتجات التابعة للفئة الرئيسية المختارة
                const filteredProducts = allProducts.filter(product =>
                    subs.some(sub => sub.id === product.productSubId)
                );
                setProducts(filteredProducts);
            }
        }

        // تحديث الفئة الفرعية النشطة
        setActiveSubId(prev => ({
            ...prev,
            [mainId]: subId
        }));
    };

    const getFilteredProductsForMain = (mainId: string) => {
        // Get all products for this main category
        const mainProducts = allProducts.filter(product => {
            const sub = allProductSubs.find(s => s.id === product.productSubId);
            return sub?.productMainId === mainId;
        });

        // If a subcategory is selected for this main category, filter by it
        if (activeSubId[mainId]) {
            return mainProducts.filter(product => product.productSubId === activeSubId[mainId]);
        }

        return mainProducts;
    };

    const getProductsForSubCategory = (subId: string) => {
        return allProducts.filter(product =>
            product.productSubId === subId &&
            product.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const handleAddToCart = (product: Product) => {
        if (!item?.whatsapp) {
            toast.error('عذراً، هذا المتجر لا يدعم الطلب عبر الواتساب', { autoClose: 500 });
            return;
        }

        const whatsappNumber = item.whatsapp.split(',')[0];

        addToCart({
            ...product,
            itemId: id!,
            whatsappNumber: whatsappNumber
        });

        toast.success(`تم إضافة ${product.quantity} ${product.title} للسلة 🛍️`, { autoClose: 500 });
    };

    const filteredProducts = products.filter(product =>
        product.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // وظيفة المتابعة
    const handleFollow = async (e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!item) return;

        if (!isAuthenticated) {
            setIsLoginModalOpen(true);
            return;
        }

        try {
            //console.log('Handling follow for item:', item.id);
            if (isFollowing(item.id)) {
                await unfollowItem(item.id);
                //console.log('Successfully unfollowed item:', item.id);
            } else {
                await followItem(item.id);
                //console.log('Successfully followed item:', item.id);
            }
        } catch (error) {
            console.error('Error following/unfollowing item:', error);
        }
    };

    if (loading) return <LoadingOverlay />;
    if (!item) return <div>لا يوجد بيانات</div>;

    return (
        <>
            <SEOHead
                title={item.Meta_Title || `${item.title} - وصال سيتي`}
                description={item.Meta_Description || item.description}
                keywords={item.Meta_key_Words || `وصال سيتي, ${item.title}`}
                image={item.images?.[0]}
                url={window.location.href}
                type="article"
            />
            <div className="item-details">
                <div className="back-button-container">
                    <button onClick={() => navigate(-1)} className="back-button">
                        <svg viewBox="0 0 24 24">
                            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                        </svg>
                        <span>رجوع</span>
                    </button>
                    <button onClick={() => navigate('/')} className="home-button">
                        <svg viewBox="0 0 24 24">
                            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                        </svg>
                        <span>القائمة الرئيسية</span>
                    </button>
                    <button
                        className={`follow-button ${isFollowing(item.id) ? 'following' : ''}`}
                        onClick={handleFollow}
                    >
                        {isFollowing(item.id) ? 'إلغاء المتابعة' : 'متابعة'}
                    </button>
                </div>
                <div className="item-header">
                    <div className="item-header-content">
                        <h1 className="item-title">{item.title}</h1>
                        <div className="status-indicators">
                            {item.isOpen && (
                                <span className="status-icon" title="مفتوح 24/7">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                                    </svg>
                                </span>
                            )}
                            {item.delivery && (
                                <span className="status-icon" title="خدمة التوصيل متوفرة">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M19 7c0-1.1-.9-2-2-2h-3v2h3v2.65L13.52 14H10V9H6c-2.21 0-4 1.79-4 4v3h2c0 1.66 1.34 3 3 3s3-1.34 3-3h4.48L19 10.35V7zM7 17c-.55 0-1-.45-1-1h2c0 .55-.45 1-1 1z" />
                                    </svg>
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="gallery-section">
                    {item.images.length > 0 && (
                        <>
                            <div className="main-image">
                                <ImageWithFallback src={selectedImage} alt={item.title} className="main-image-test" />
                            </div>
                            <div className="thumbnails">
                                {item.images.map((image, index) => (
                                    <div
                                        key={index}
                                        className={`thumbnail ${selectedImage === image ? 'active' : ''}`}
                                        onClick={() => setSelectedImage(image)}
                                    >
                                        <ImageWithFallback src={image} alt={`${item.title} ${index + 1}`} />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>


                <div className="description-section">
                    <h2>الوصف</h2>
                    <p>{item.description}</p>
                </div>

                <div className="contact-section">
                    <h2>معلومات التواصل</h2>
                    <div className="contact-icons-grid">
                        {item.phone?.split(',').map((phone, index) => (
                            <a key={index} href={`tel:${phone}`} className="contact-icon-item phone" title="اتصل بنا">
                                <svg viewBox="0 0 24 24">
                                    <path d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                                </svg>
                            </a>
                        ))}

                        {item.whatsapp?.split(',').map((whatsapp, index) => (
                            <a key={index} href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer" className="contact-icon-item whatsapp" title="واتساب">
                                <svg viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                </svg>
                            </a>
                        ))}

                        {item.location && (
                            <a href={item.location} target="_blank" rel="noopener noreferrer" className="contact-icon-item map" title="الموقع">
                                <svg viewBox="0 0 24 24">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zM7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 2.88-2.88 7.19-5 9.88C9.92 16.21 7 11.85 7 9z" />
                                </svg>
                            </a>
                        )}

                        {item.facebook && (
                            <a href={item.facebook} target="_blank" rel="noopener noreferrer" className="contact-icon-item facebook" title="فيسبوك">
                                <svg viewBox="0 0 24 24">
                                    <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z" />
                                </svg>
                            </a>
                        )}

                        {item.website && (
                            <a href={item.website} target="_blank" rel="noopener noreferrer" className="contact-icon-item website" title="الموقع الإلكتروني">
                                <svg viewBox="0 0 24 24">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                                </svg>
                            </a>
                        )}
                    </div>
                </div>

                <div className="products-section">
                    <h2>المنتجات</h2>

                    <div className="search-container">
                        <div className="search-bar">
                            <svg className="search-icon" viewBox="0 0 24 24">
                                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="ابحث..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                            {searchQuery && (
                                <button
                                    className="clear-search"
                                    onClick={() => setSearchQuery('')}
                                >
                                    <span>×</span>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="main-categories-tabs">
                        <button
                            className={`main-category-tab ${activeMainId === 'all' ? 'active' : ''}`}
                            onClick={() => handleMainCategoryClick('all')}
                        >
                            الكل
                        </button>
                        {productMains.map(main => (
                            <button
                                key={main.id}
                                className={`main-category-tab ${activeMainId === main.id ? 'active' : ''}`}
                                onClick={() => handleMainCategoryClick(main.id)}
                            >
                                <ImageWithFallback src={main.iconUrl} alt="" className="tab-icon" hideOnError={true} />
                                <span>{main.title}</span>
                            </button>
                        ))}
                    </div>

                    {activeMainId === 'all' ? (
                        // عرض جميع الفئات الرئيسية مع منتجاتها
                        productMains.map(main => {
                            const mainProducts = getFilteredProductsForMain(main.id)
                                .filter(product => product.title.toLowerCase().includes(searchQuery.toLowerCase()));

                            if (mainProducts.length === 0) return null;

                            return (
                                <div key={main.id} className="category-section">
                                    <h3 className="category-title">{main.title}</h3>
                                    <div className="sub-categories-tabs">
                                        <button
                                            className={`sub-category-tab main-all-tab ${!activeSubId[main.id] ? 'active' : ''}`}
                                            onClick={() => handleSubCategoryClick(main.id, null)}
                                        >
                                            الكل
                                        </button>
                                        {productSubs
                                            .filter(sub => {
                                                const hasProducts = allProducts.some(product => product.productSubId === sub.id);
                                                return sub.productMainId === main.id && hasProducts;
                                            })
                                            .map(sub => (
                                                <button
                                                    key={sub.id}
                                                    className={`sub-category-tab ${activeSubId[main.id] === sub.id ? 'active' : ''}`}
                                                    onClick={() => handleSubCategoryClick(main.id, sub.id)}
                                                >
                                                    {sub.title}
                                                </button>
                                            ))
                                        }
                                    </div>

                                    {activeSubId[main.id] ? (
                                        // عرض منتجات الفئة الفرعية المحددة فقط
                                        <div className="sub-category-products">
                                            <h4 className="sub-category-title">
                                                {productSubs.find(sub => sub.id === activeSubId[main.id])?.title}
                                            </h4>
                                            <div className="products-grid">
                                                {allProducts
                                                    .filter(product =>
                                                        product.productSubId === activeSubId[main.id] &&
                                                        product.title.toLowerCase().includes(searchQuery.toLowerCase())
                                                    )
                                                    .map(product => (
                                                        <ProductCard
                                                            key={product.id}
                                                            product={product}
                                                            onAddToCart={handleAddToCart}
                                                        />
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    ) : (
                                        // عرض جميع الفئات الفرعية مع منتجاتها
                                        <div className="all-subcategories">
                                            {productSubs
                                                .filter(sub => {
                                                    const hasProducts = allProducts.some(product =>
                                                        product.productSubId === sub.id &&
                                                        product.title.toLowerCase().includes(searchQuery.toLowerCase())
                                                    );
                                                    return sub.productMainId === main.id && hasProducts;
                                                })
                                                .map(sub => {
                                                    const subProducts = getProductsForSubCategory(sub.id);
                                                    if (subProducts.length === 0) return null;

                                                    return (
                                                        <div key={sub.id} className="sub-category-section">
                                                            <h4 className="sub-category-title">{sub.title}</h4>
                                                            <div className="products-grid">
                                                                {subProducts.map(product => (
                                                                    <ProductCard
                                                                        key={product.id}
                                                                        product={product}
                                                                        onAddToCart={handleAddToCart}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            }
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        // عرض الفئة الرئيسية المحددة مع الفئات الفرعية والمنتجات
                        <div className="category-section">
                            <h3 className="category-title">{productMains.find(main => main.id === activeMainId)?.title}</h3>

                            {/* عرض أزرار الفئات الفرعية في الأعلى */}
                            <div className="sub-categories-tabs">
                                <button
                                    className={`sub-category-tab main-all-tab ${!activeSubId[activeMainId] ? 'active' : ''}`}
                                    onClick={() => handleSubCategoryClick(activeMainId, null)}
                                >
                                    الكل
                                </button>
                                {productSubs
                                    .filter(sub => {
                                        const hasProducts = allProducts.some(product => product.productSubId === sub.id);
                                        return sub.productMainId === activeMainId && hasProducts;
                                    })
                                    .map(sub => (
                                        <button
                                            key={sub.id}
                                            className={`sub-category-tab ${activeSubId[activeMainId] === sub.id ? 'active' : ''}`}
                                            onClick={() => handleSubCategoryClick(activeMainId, sub.id)}
                                        >
                                            {sub.title}
                                        </button>
                                    ))
                                }
                            </div>

                            {/* إذا تم اختيار فئة فرعية محددة، عرض منتجاتها فقط */}
                            {activeSubId[activeMainId] ? (
                                <div className="sub-category-products">
                                    <h4 className="sub-category-title">
                                        {productSubs.find(sub => sub.id === activeSubId[activeMainId])?.title}
                                    </h4>
                                    <div className="products-grid">
                                        {getProductsForSubCategory(activeSubId[activeMainId] as string).map(product => (
                                            <ProductCard
                                                key={product.id}
                                                product={product}
                                                onAddToCart={handleAddToCart}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                // إذا لم يتم اختيار فئة فرعية، عرض جميع الفئات الفرعية مع منتجاتها
                                <div className="all-subcategories">
                                    {productSubs
                                        .filter(sub => {
                                            const hasProducts = allProducts.some(product =>
                                                product.productSubId === sub.id &&
                                                product.title.toLowerCase().includes(searchQuery.toLowerCase())
                                            );
                                            return sub.productMainId === activeMainId && hasProducts;
                                        })
                                        .map(sub => {
                                            const subProducts = getProductsForSubCategory(sub.id);
                                            if (subProducts.length === 0) return null;

                                            return (
                                                <div key={sub.id} className="sub-category-section">
                                                    <h4 className="sub-category-title">{sub.title}</h4>
                                                    <div className="products-grid">
                                                        {subProducts.map(product => (
                                                            <ProductCard
                                                                key={product.id}
                                                                product={product}
                                                                onAddToCart={handleAddToCart}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    }
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* إضافة زر المتابعة في مكان أفضل */}

            </div>

            {/* استخدام مكون LoginModal بدلاً من إنشاء مودال مخصص */}
            {isLoginModalOpen && (
                <LoginModal
                    isOpen={isLoginModalOpen}
                    onClose={() => setIsLoginModalOpen(false)}
                />
            )}
        </>
    );
};

const ProductCard: React.FC<{
    product: Product;
    onAddToCart: (product: Product) => void;
}> = ({ product, onAddToCart }) => {
    const [quantity, setQuantity] = useState(1);

    const handleQuantityChange = (change: number) => {
        const newQuantity = Math.max(1, quantity + change);
        setQuantity(newQuantity);
    };

    return (
        <div className="product-card">
            <ImageWithFallback src={product.imageUrl} alt={product.title} />
            <h3>{product.title}</h3>
            <div className="price">
                {product.salePrice ? (
                    <>
                        <span className="sale-price">{product.salePrice} ج.م</span>
                        <span className="original-price">{product.mainPrice} ج.م</span>
                    </>
                ) : (
                    <span className="main-price">{product.mainPrice} ج.م</span>
                )}
            </div>
            <div className="quantity-controls">
                <button onClick={() => handleQuantityChange(1)}>+</button>
                <span>{quantity}</span>
                <button onClick={() => handleQuantityChange(-1)}>-</button>
            </div>
            <div className="total-price">
                <span>الإجمالي: </span>
                <span>
                    {((product.salePrice || product.mainPrice) * quantity).toFixed(2)} ج.م
                </span>
            </div>
            <button
                className="add-to-cart"
                onClick={() => {
                    onAddToCart({ ...product, quantity });
                    setQuantity(1);
                }}
            >
                إضافة للسلة
            </button>
        </div>
    );
};

export default ItemDetails; 